import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {J1Page} from '@/pages/components/audit-program/j-section/constants';
import {J1_PAGE} from '@/pages/components/audit-program/j-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface J1SummaryViewProps {
    header: AuditProgramSectionVO;
    page: J1Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
/** Contain InputField default minWidth:110 so adjacent cells don't overlap */
const bodySx = {...cellBorder, py: 0.5, px: 0.5, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** J1 — Deposit paid and prepayment lead summary. */
export default function J1SummaryView({
    header,
    page,
    refCode = 'J1',
    values,
    onChange,
}: J1SummaryViewProps) {
    const meta = {
        title: page.title || J1_PAGE.title,
        cyLabel: page.cyLabel || J1_PAGE.cyLabel,
        lyLabel: page.lyLabel || J1_PAGE.lyLabel,
        summaryLines: page.summaryLines?.length ? page.summaryLines : J1_PAGE.summaryLines,
        conclusionLabel: page.conclusionLabel || J1_PAGE.conclusionLabel,
        conclusionPlaceholder: page.conclusionPlaceholder || J1_PAGE.conclusionPlaceholder,
        remarksTitle: page.remarksTitle || J1_PAGE.remarksTitle,
        remarks: page.remarks?.length ? page.remarks : J1_PAGE.remarks,
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <TableContainer sx={{mb: 2, maxWidth: 720}}>
                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 48}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 140}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.cyLabel}</TableCell>
                            <TableCell sx={headSx} align="right">{meta.lyLabel}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {meta.summaryLines.map((line) => (
                            <TableRow key={line.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2">{line.label}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        value={values[`${line.id}.tick`] ?? ''}
                                        onChange={(e) => onChange(`${line.id}.tick`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="right"
                                        sx={fieldSx}
                                        value={values[`${line.id}.cy`] ?? ''}
                                        onChange={(e) => onChange(`${line.id}.cy`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="right"
                                        sx={fieldSx}
                                        value={values[`${line.id}.ly`] ?? ''}
                                        onChange={(e) => onChange(`${line.id}.ly`, e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack spacing={0.75} sx={{mb: 2, maxWidth: 720}}>
                <Typography variant="subtitle2" fontWeight={700}>{meta.conclusionLabel}</Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    multiline
                    minRows={2}
                    placeholder={meta.conclusionPlaceholder}
                    value={values.conclusion ?? ''}
                    onChange={(e) => onChange('conclusion', e.target.value)}
                />
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {meta.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {meta.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 40, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
